import { BizData, Contact, LastEmploymentData } from "../../../../interfaces/DTO/crm/contact";
import { usePartialUpdateContact } from "../../../../hooks/crm/useContacts";
import { EmploymentRecord, EmploymentRecordKind } from "./EmploymentRecordCard";

/**
 * Saves a contact's last employment - workplaces (data.last_employment_data), businesses (data.biz_data) and the
 * company id (primary_biz_id). The whole data is sent (a partial update replaces it as is); an empty list is removed.
 */
export const useContactEmploymentSave = (contact: Contact) => {
  const updateContact = usePartialUpdateContact();

  const employments = contact.data?.last_employment_data ?? [];
  const businesses = contact.data?.biz_data ?? [];

  const saveList = (key: "last_employment_data" | "biz_data", list: unknown[]) => {
    const data: Record<string, unknown> = { ...(contact.data ?? {}) };
    if (list.length > 0) data[key] = list;
    else delete data[key];
    return updateContact.mutateAsync({ id: contact.id!, fields: { data } as Partial<Contact> });
  };

  const saveCompanyId = (value: string) =>
    updateContact.mutateAsync({ id: contact.id!, fields: { primary_biz_id: (value || null) as unknown as string } });

  /** a new record of the kind */
  const add = (kind: EmploymentRecordKind, record: EmploymentRecord) => {
    if (kind === "employment") return saveList("last_employment_data", [...employments, record as LastEmploymentData]);
    if (kind === "business") return saveList("biz_data", [...businesses, record as BizData]);
    return saveCompanyId(String(record.primary_biz_id ?? ""));
  };

  const updateEmployment = (index: number, record: EmploymentRecord) =>
    saveList("last_employment_data", employments.map((e, i) => (i === index ? (record as LastEmploymentData) : e)));
  const deleteEmployment = (index: number) => saveList("last_employment_data", employments.filter((_, i) => i !== index));
  const updateBusiness = (index: number, record: EmploymentRecord) =>
    saveList("biz_data", businesses.map((b, i) => (i === index ? (record as BizData) : b)));
  const deleteBusiness = (index: number) => saveList("biz_data", businesses.filter((_, i) => i !== index));

  return { add, updateEmployment, deleteEmployment, updateBusiness, deleteBusiness, saveCompanyId };
};
